import { DEFAULT_THEME, MantineColorsTuple } from '@mantine/core';

type ColorResult = { [key: string]: string };

const sizes = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

const combinedColors = (colors: string[] | MantineColorsTuple) => {
  const result: ColorResult = {};

  colors.forEach((color, index) => {
    const size = sizes[index];
    result[size.toString()] = color;
  });

  return result;
};

/**
 * Extend default Tailwind color palette with missing Mantine colors
 * NOTE: Mantine supports only 10 colors in each color-shade
 */
const colors: { [key: string]: ColorResult | string } = {
  white: '#fff',
  black: '#000',
  transparent: 'transparent',
  dark: combinedColors([
    '#F9FBFF',
    '#F4F7FE',
    '#EEEFF8',
    '#DCDEEB',
    '#B7BBD6',
    '#8E94B1',
    '#777C97',
    '#5E6278',
    '#2D2B38',
    '#0E0D13',
  ]),
  gray: combinedColors([
    '#F9FAFB',
    '#F2F4F7',
    '#E4E7EC',
    '#D0D5DD',
    '#98A2B3',
    '#667085',
    '#475467',
    '#344054',
    '#1D2939',
    '#101828',
  ]),
  red: combinedColors([
    '#FEF3F2',
    '#FEE4E2',
    '#FECDCA',
    '#FDA29B',
    '#F97066',
    '#F04438',
    '#D92D20',
    '#B42318',
    '#912018',
    '#7A271A',
  ]),
  pink: combinedColors([
    '#FEF5F9',
    '#FDECF5',
    '#FDDAED',
    '#FCBADC',
    '#FB7185',
    '#EC4899',
    '#DB2777',
    '#CC3770',
    '#AF2B5F',
    '#972955',
  ]),
  grape: combinedColors(DEFAULT_THEME.colors['grape']),
  violet: combinedColors([
    '#FBF9FF',
    '#F1EEFF',
    '#DED9FF',
    '#C8BFFF',
    '#A395FC',
    '#7C68FB',
    '#705EE2',
    '#6454CB',
    '#5A4CB7',
    '#5144A5',
  ]),
  indigo: combinedColors([
    '#F2F4FF',
    '#E4E8FF',
    '#DBE1FF',
    '#C6CEFE',
    '#B1B5EC',
    '#8E94EB',
    '#6C75FC',
    '#525DFF',
    '#9059f1',
    '#343CA1',
  ]),
  blue: combinedColors([
    '#E6F2FF',
    '#CAE3FE',
    '#C3E1FF',
    '#90C7FF',
    '#6CB6FF',
    '#2994FF',
    '#1885F2',
    '#076FD7',
    '#1562B0',
    '#0A5098',
  ]),
  cyan: combinedColors(DEFAULT_THEME.colors['cyan']),
  teal: combinedColors(DEFAULT_THEME.colors['teal']),
  green: combinedColors([
    '#ECFDF3',
    '#D1FADF',
    '#A6F4C5',
    '#6CE9A6',
    '#32D583',
    '#12B76A',
    '#039855',
    '#027A48',
    '#05603A',
    '#054F31',
  ]),
  lime: combinedColors(DEFAULT_THEME.colors['lime']),
  yellow: combinedColors([
    '#FFFCEF',
    '#FEF5D1',
    '#FEE99B',
    '#FDD95F',
    '#F8AD04',
    '#F8AD04',
    '#E28A00',
    '#C36702',
    '#A4530D',
    '#8C4613',
  ]),
  orange: combinedColors(DEFAULT_THEME.colors['orange']),
  purple: combinedColors(['#E4E8FF', '#4851DE']),
};

const fontFamily =
  'poppins,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";';

const fontFamilyMonospace =
  'poppins,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"';

const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

const colorsConfig = {
  white: '#fff',
  black: '#000',
  colors,
  radius: {
    ...DEFAULT_THEME.radius,
    full: 9999,
  },
  fontFamily,
  fontFamilyMonospace,
  breakpoints,
};

export default colorsConfig;
